import { SetURLSearchParams } from 'react-router-dom'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { ChartCard } from '../../components/cards/ChartCard'
import CountCard from '../../components/cards/CountCard'
import { formatCurrency, prettyFormatNumber } from '../../helpers'
import { useAppSelector } from '../../hooks'
import { useGetSingleVendorsMetricsQuery } from '../../redux-toolkits/vendors/vendors.slice'
import { DateFilterQueryProps } from '../../redux-toolkits/shared/shared.type'
export const SingleVendorMetrics = ({
  id,
  searchParams,
  setSearchParams,
  dateFilterProps,
}: {
  id: string
  searchParams: URLSearchParams
  setSearchParams: SetURLSearchParams
  dateFilterProps: DateFilterQueryProps
}) => {
  const { selectedFulfilmentCenters } = useAppSelector((state) => state.auth)

  const { data: vendorMetrics } = useGetSingleVendorsMetricsQuery(
    {
      ...dateFilterProps,
      id: id,
      fulfillmentCentreId:
        selectedFulfilmentCenters.map((c) => c._id).join(',') || undefined,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  )

  const avgDelivery = vendorMetrics?.data?.averageDeliveryTime
  const completedOrders = avgDelivery?.totalCompletedOrders ?? 0
  const orderLabel = `${completedOrders.toLocaleString()} ${
    completedOrders === 1 ? 'order' : 'orders'
  }`
  const totalMins = avgDelivery?.averageDurationMinutes ?? 0
  const displayHours = Math.floor(totalMins / 60)
  const displayMins = Math.round(totalMins % 60)

  return (
    <div className="flex flex-col gap-10">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <CountCard
          count={vendorMetrics?.data?.totalRevenue ?? 0}
          text="Total Revenue"
          isCurrency={true}
        />
        <CountCard
          count={vendorMetrics?.data?.totalOrderCount ?? 0}
          text="Total Order Count"
          isCurrency={false}
        />
        <CountCard
          count={vendorMetrics?.data?.totalCustomers ?? 0}
          text="Total Number of Customers"
          isCurrency={false}
        />
        <CountCard
          count={displayHours}
          valueSuffix={`h ${displayMins}m`}
          text={`Avg. Delivery Time (${orderLabel})`}
          isCurrency={false}
        />
      </div>

      <ChartCard title="Top Items by Revenue" value={undefined}>
        <ResponsiveContainer width={'100%'} height={240} className={'mt-6'}>
          <BarChart
            data={
              (vendorMetrics?.data?.topProducts?.length ?? 0) > 0
                ? vendorMetrics?.data?.topProducts?.map((entry) => {
                    return {
                      label: entry.productName,
                      revenue: entry.totalRevenue,
                      orders: entry.totalQuantity,
                    }
                  })
                : [{ label: '', revenue: 0, orders: 0 }]
            }
            margin={{
              top: 0,
              right: 0,
              left: 0,
              bottom: 0,
            }}
          >
            <CartesianGrid
              strokeDasharray="3 0"
              vertical={true}
              stroke="#EAEAEA"
            />

            <YAxis
              dataKey="revenue"
              axisLine={false}
              tickLine={false}
              type="number"
              style={{
                fontSize: '12px',
                fontWeight: 'normal',
                color: '#555555',
              }}
              tickFormatter={(val) => prettyFormatNumber(val)}
            />

            <XAxis
              dataKey="label"
              axisLine={false}
              tickLine={false}
              interval={0}
              style={{
                fontSize: '12px',
                fontWeight: 'normal',
                color: '#555555',
              }}
              tickFormatter={(val) => val.slice(0, 8) + '...'}
            />

            <Tooltip
              content={({ payload }) => {
                if (!payload?.length) return null
                const dateLabel = payload[0]?.payload?.label
                const revenue = payload[0]?.payload?.revenue
                const totalOrder = payload[0]?.payload?.orders

                return (
                  <div className="flex flex-col gap-2 rounded-[8px] bg-[#F3F3F3] p-2 text-left text-xs max-w-[250px]">
                    <p className="text-sec-black">{dateLabel}</p>
                    <p className="font-bold text-base text-black">
                      {payload ? `${formatCurrency(revenue ?? 0, {})}` : ''}
                    </p>
                    <p className="text-sec-black">
                      {payload ? `Total order count: ${totalOrder}` : 0}
                    </p>
                  </div>
                )
              }}
              cursor={{
                stroke: '#F45D2C',
                strokeWidth: 1,
                strokeDasharray: '8 8',
                fill: 'transparent',
              }}
            />

            <Bar
              type="linear"
              dataKey="revenue"
              stroke={undefined}
              strokeWidth={2}
              fillOpacity={1}
              fill="#F45D2C"
              maxBarSize={24}
              radius={[6, 6, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  )
}
